/**
 * Research Scope API
 * 
 * API functions for managing Research Scopes - personal document collections
 * with Azure AI Search agentic retrieval.
 */

import { authenticatedFetch } from '../utils/authApi';
import { cachedApiService } from '../services/CachedApiService';
import { actionsMenuCache } from '../components/ActionsMenu/ActionsMenu';

// ============================================================================
// Types
// ============================================================================

export interface ResearchScope {
  id: string;
  name: string;
  description: string;
  icon: string;
  file_count: number;
  total_size_bytes: number;
  is_owner: boolean;
  is_public: boolean;
  status: 'empty' | 'indexing' | 'ready' | 'error';
  created_at: string;
  updated_at: string;
}

export interface ResearchScopeDetail extends ResearchScope {
  user_id: string;
  container_name: string;
  knowledge_source_name: string;
  knowledge_base_name: string;
  files: ScopeFile[];
  shared_with_users: string[];
  shared_with_groups: string[];
  retrieval_instructions: string;
  answer_instructions: string;
  reasoning_effort?: string;  // low, medium (default: low)
  max_output_size?: number;   // Max output tokens (default: 5000)
}

export interface ScopeFile {
  id: string;
  filename: string;
  blob_path: string;
  blob_url: string;
  content_type: string;
  size_bytes: number;
  file_type: string;
  requires_ocr: boolean;
  ocr_provider: string | null;
  uploaded_at: string;
  uploaded_by: string;
  processing_status: 'uploaded' | 'indexing' | 'indexed';
  index_error: string | null;
}

export interface ScopeStatus {
  success: boolean;
  scope_id: string;
  scope_name: string;
  file_count: number;
  total_size_bytes: number;
  created_at: string;
  updated_at: string;
  knowledge_source: {
    name: string;
    exists: boolean;
    info: Record<string, unknown> | null;
    error: string | null;
  };
  knowledge_base: {
    name: string;
    exists: boolean;
    info: Record<string, unknown> | null;
  };
  status: 'empty' | 'indexing' | 'ready' | 'error';
}

export interface CreateScopeRequest {
  name: string;
  description?: string;
  icon?: string;
  retrieval_instructions?: string;
  answer_instructions?: string;
}

export interface UpdateScopeRequest {
  name?: string;
  description?: string;
  icon?: string;
  retrieval_instructions?: string;
  answer_instructions?: string;
  reasoning_effort?: string;
  max_output_size?: number;
  shared_with_users?: string[];
  shared_with_groups?: string[];
  is_public?: boolean;
}

export interface GeneratedInstructions {
  retrieval_instructions: string;
  answer_instructions: string;
}

// ============================================================================
// API Functions
// ============================================================================

const API_BASE = '/api/research-scopes';

/**
 * Get all Research Scopes the user has access to
 */
export async function getResearchScopes(includeShared: boolean = true): Promise<ResearchScope[]> {
  const url = `${API_BASE}?include_shared=${includeShared}`;
  const response = await cachedApiService.cachedFetch(url);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch research scopes: ${response.statusText}`);
  }
  
  const data = await response.json();
  return data.scopes || [];
}

/**
 * Get a single Research Scope by ID
 */
export async function getResearchScope(scopeId: string): Promise<ResearchScopeDetail> {
  const response = await cachedApiService.cachedFetch(`${API_BASE}/${scopeId}`);
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Research scope not found');
    }
    throw new Error(`Failed to fetch research scope: ${response.statusText}`);
  }
  
  const data = await response.json();
  return data.scope;
}

/**
 * Create a new Research Scope
 */
export async function createResearchScope(request: CreateScopeRequest): Promise<ResearchScopeDetail> {
  const response = await authenticatedFetch(API_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request)
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to create research scope: ${response.statusText}`);
  }
  
  // Invalidate cache
  cachedApiService.invalidate(API_BASE);
  actionsMenuCache.invalidateScopes();
  
  const data = await response.json();
  return data.scope;
}

/**
 * Update a Research Scope
 */
export async function updateResearchScope(
  scopeId: string, 
  request: UpdateScopeRequest
): Promise<ResearchScopeDetail> {
  const response = await authenticatedFetch(`${API_BASE}/${scopeId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request)
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to update research scope: ${response.statusText}`);
  }
  
  // Invalidate cache
  cachedApiService.invalidate(API_BASE);
  cachedApiService.invalidate(`${API_BASE}/${scopeId}`);
  
  const data = await response.json();
  return data.scope;
}

/**
 * Delete a Research Scope
 */
export async function deleteResearchScope(scopeId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/${scopeId}`, {
    method: 'DELETE'
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to delete research scope: ${response.statusText}`);
  }
  
  // Invalidate cache
  cachedApiService.invalidate(API_BASE);
  actionsMenuCache.invalidateScopes();
}

/**
 * Get the status of a Research Scope (indexing progress)
 */
export async function getResearchScopeStatus(scopeId: string): Promise<ScopeStatus> {
  // Don't cache status - we want fresh data
  const response = await authenticatedFetch(`${API_BASE}/${scopeId}/status`);
  
  if (!response.ok) {
    throw new Error(`Failed to fetch scope status: ${response.statusText}`);
  }
  
  return await response.json();
}

/**
 * Upload a document to a Research Scope
 */
export async function uploadDocument(scopeId: string, file: File): Promise<ScopeFile> {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await authenticatedFetch(`${API_BASE}/${scopeId}/documents`, {
    method: 'POST',
    body: formData
    // Don't set Content-Type - browser will set it with boundary
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to upload document: ${response.statusText}`);
  }
  
  // Invalidate cache
  cachedApiService.invalidate(`${API_BASE}/${scopeId}`);
  
  const data = await response.json();
  return data.file;
}

/**
 * Delete a document from a Research Scope
 */
export async function deleteDocument(scopeId: string, fileId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE}/${scopeId}/documents/${fileId}`, {
    method: 'DELETE'
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to delete document: ${response.statusText}`);
  }
  
  // Invalidate cache
  cachedApiService.invalidate(`${API_BASE}/${scopeId}`);
}

/**
 * Query a Research Scope using agentic retrieval
 */
export async function queryResearchScope(scopeId: string, query: string): Promise<{
  response: string;
  sources: Array<{ title: string; content: string; citation: string }>;
}> {
  const response = await authenticatedFetch(`${API_BASE}/${scopeId}/query`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query })
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to query research scope: ${response.statusText}`);
  }
  
  return await response.json();
}

/**
 * Generate AI-powered instructions for a Research Scope
 */
export async function generateInstructions(scopeId: string): Promise<GeneratedInstructions> {
  const response = await authenticatedFetch(`${API_BASE}/${scopeId}/generate-instructions`, {
    method: 'POST'
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to generate instructions: ${response.statusText}`);
  }
  
  const data = await response.json();
  return {
    retrieval_instructions: data.retrieval_instructions,
    answer_instructions: data.answer_instructions
  };
}

/**
 * Enhance a description using AI to generate better instructions later
 */
export async function enhanceDescription(name: string, description: string): Promise<string> {
  const response = await authenticatedFetch(`${API_BASE}/enhance-description`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, description })
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Failed to enhance description: ${response.statusText}`);
  }
  
  const data = await response.json();
  return data.enhanced_description;
}
